import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { testBillComponentConfig, debugBillComponentCreation } from "@/components/pricing/create-bill-component";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Check, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

export function ConfigAndConnectionTest() {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [schoolId, setSchoolId] = useState("12345");
  const [amount, setAmount] = useState("10.00");
  const [notes, setNotes] = useState("Test bill from configuration test");
  const { toast } = useToast();

  const handleTestConfig = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);
    
    try {
      const data = await testBillComponentConfig();
      setResponse(data);
      
      toast({
        title: "Success",
        description: "Configuration test successful",
      });
    } catch (error: any) {
      console.error('Configuration test error:', error);
      setError(error.message || "Failed to test configuration");
      
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to test configuration",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleTestConnection = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);
    
    try {
      const data = await debugBillComponentCreation(
        schoolId.trim(),
        parseFloat(amount),
        notes
      );
      
      setResponse(data);
      
      toast({
        title: "Success",
        description: "Connection test successful using infradigital function",
      });
    } catch (error: any) {
      console.error('Connection test error:', error);
      setError(error.message || "Failed to test connection");
      
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to test connection",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Infradigital Config & Connection Test</CardTitle>
        <CardDescription>
          Test the Infradigital API configuration and connection
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label htmlFor="schoolId">School ID</Label>
              <Input 
                id="schoolId"
                value={schoolId}
                onChange={(e) => setSchoolId(e.target.value)}
                placeholder="School ID"
              />
            </div>
            <div>
              <Label htmlFor="amount">Amount</Label>
              <Input 
                id="amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Amount"
                type="number"
                step="0.01"
              />
            </div>
          </div>
          <div className="mb-4">
            <Label htmlFor="notes">Notes</Label>
            <Textarea 
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Test notes"
              rows={2}
            />
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4">
          <Button 
            onClick={handleTestConfig} 
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>Test Configuration</>
            )}
          </Button>
          
          <Button 
            onClick={handleTestConnection}
            disabled={loading}
            variant="default"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>Test Connection</>
            )}
          </Button>
        </div>

        {error && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription className="mt-2 whitespace-pre-wrap">
              {error}
            </AlertDescription>
          </Alert>
        )}

        {response && (
          <div className="mt-4">
            <Alert variant="default" className="bg-green-50 border-green-200">
              <Check className="h-4 w-4 text-green-500" />
              <AlertTitle className="text-green-700">Success</AlertTitle>
              <AlertDescription className="mt-2">
                Test completed successfully
              </AlertDescription>
            </Alert>
            
            <div className="mt-4">
              <h3 className="text-sm font-medium mb-2">API Response:</h3>
              <pre className="bg-gray-100 dark:bg-gray-900 p-4 rounded-lg overflow-auto max-h-96 text-xs">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
